/* React */
import React, { useRef, useState } from "react";

/* Ionic */
import {
  IonItem,
  InputInputEventDetail,
} from "@ionic/react";
import type { IonInputCustomEvent } from "@ionic/core";
import { chevronDown, chevronUp } from "ionicons/icons";

/* Utils */
import useClickOutsideHandler from "src/utils/hooks/useClickOutsideHandler";

/* Components */
import PrimaryFabButton from "src/components/shared/buttons/primary-fab-button/PrimaryFabButton";
import FilterLabel from "src/components/shared/labels/filter-label/FilterLabel";
import ItemsContainer from "src/components/insights/custom-cohorts/cohort-builder/entity-selector/select/items-container/ItemsContainer";
import Searchbar from "src/components/insights/custom-cohorts/cohort-builder/entity-selector/select/searchbar/Searchbar";

/* Stylesheet */
import styles from "./Select.module.scss";

/* Interfaces */
import { BasicProps } from "src/shared/interfaces/custom-cohorts/CustomCohorts";

interface SelectProps {
  label?: string;
  value: string;
  items: BasicProps[];
  disabled: boolean;
  onClick: (item: BasicProps) => void;
}

const Select: React.FC<SelectProps> = ({
  label,
  value,
  items,
  disabled,
  onClick,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showItems, setShowItems] =
    useState<boolean>(false);
  const [showSearchbar, setShowSearchbar] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleSearchbar = (value: boolean) => {
    if (!value) {
      setSearchTerm("");
    }
    setShowSearchbar(value);
  };

  const handleShowItems = (value: boolean) => {
    setShowItems(value);
    if (!value) {
      setSearchTerm("");
    }
  };

  const handleSearchTerm = (
    e: IonInputCustomEvent<InputInputEventDetail>
  ) => {
    setSearchTerm(e.target.value as string);
  };

  useClickOutsideHandler({
    ref: dropdownRef,
    onClick: handleShowItems,
  });

  // Filter items which are already selected
  const filteredItems = (
    value: string,
    items: BasicProps[]
  ) => {
    return items?.filter(
      (item) => item.entity.name !== value
    );
  };

  // Filter items on a given search term
  const searchItems = items?.filter((item) => {
    return item.entity.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  return (
    <div ref={dropdownRef} className={styles.select}>
      {label ? (
        <FilterLabel
          scssProps={{
            color: disabled
              ? "var(--ion-color-text-disabled)"
              : "var(--ion-color-text-primary)",
          }}
          label={label}
        />
      ) : undefined}
      <IonItem
        style={{
          "--padding-start":
            showSearchbar && showItems ? "0px" : undefined,
          "--background": showItems ? "#f6f8fc" : undefined,
          "--background-hover": !showSearchbar
            ? "var(--ion-color-text-tertiary)"
            : "transparent",
          borderRadius: showItems
            ? "8px 8px 0px 0px"
            : undefined,
        }}
        className={styles.item}
        lines={"none"}
        button
        disabled={disabled}
        onClick={() => {
          handleShowItems(!showItems);
        }}
      >
        <div className={styles.value}>{value}</div>
        <PrimaryFabButton
          scssProps={{
            position: "absolute",
            margin: "0px 8px 0px 0px",
            right: 0,
            color: "var(--ion-color-text-tertiary)",
            background: "transparent",
            zIndex: 100,
          }}
          className={"small"}
          ariaLabel={"dropdown"}
          icon={showItems ? chevronUp : chevronDown}
          onClick={() => handleShowItems(!showItems)}
        />
        <Searchbar
          scssProps={{
            color: "var(--ion-color-text-tertiary)",
          }}
          showItems={showItems}
          showSearchbar={showSearchbar}
          handleSearchbar={handleSearchbar}
          handleSearchTerm={handleSearchTerm}
        />
      </IonItem>
      {showItems ? (
        <ItemsContainer
          items={filteredItems(value, searchItems)}
          handleShowItems={handleShowItems}
          disabled={disabled}
          onClick={onClick}
        />
      ) : undefined}
    </div>
  );
};

export default Select;
